import { useState, useCallback, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { useEcommerceUsage, WARNING_THRESHOLD_PERCENT } from '../../../hooks/useEcommerceUsage'
import { useHasFeature, useSelectedAccountId } from '../../../hooks/useSubscription'
import './EcommerceUsageIndicator.css'

/**
 * Format reset date for display
 */
function formatResetDate(dateStr, options = { month: 'short', day: 'numeric' }) {
  if (!dateStr) return null
  const date = new Date(dateStr)
  return date.toLocaleDateString(undefined, options)
}

/**
 * Get CSS class names for quota state
 */
function getQuotaClassName(isNearLimit, isExceeded) {
  if (isExceeded) return 'exceeded'
  if (isNearLimit) return 'warning'
  return ''
}

/**
 * Single quota display item
 */
function QuotaItem({ icon, label, used, limit, unlimited, percentage, isNearLimit, isExceeded, resetDate }) {
  const { t } = useTranslation('ecommerce')
  const stateClass = getQuotaClassName(isNearLimit, isExceeded)

  return (
    <div className="ecommerce-quota-item">
      <div className="ecommerce-quota-label">
        <span className="ecommerce-quota-name">
          <span className="ecommerce-quota-icon" aria-hidden="true">{icon}</span>
          {label}
        </span>
        <span className={`ecommerce-quota-value ${stateClass}`}>
          {unlimited ? (
            <span className="unlimited" aria-label={t('usage.unlimited', 'Unlimited')}>∞</span>
          ) : (
            `${used}/${limit}`
          )}
        </span>
      </div>
      {!unlimited && (
        <div className="ecommerce-quota-bar-container" role="progressbar" aria-valuenow={percentage} aria-valuemin="0" aria-valuemax="100">
          <div
            className={`ecommerce-quota-bar ${stateClass}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      )}
      {resetDate && !unlimited && (
        <span className="ecommerce-quota-reset">
          {t('usage.resetsOn', 'Resets')} {formatResetDate(resetDate)}
        </span>
      )}
    </div>
  )
}

QuotaItem.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  used: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  unlimited: PropTypes.bool,
  percentage: PropTypes.number.isRequired,
  isNearLimit: PropTypes.bool,
  isExceeded: PropTypes.bool,
  resetDate: PropTypes.string,
}

/**
 * Warning messages by type (defined outside component)
 */
const WARNING_MESSAGES = {
  videos: 'usage.warnings.videosNearLimit',
  products: 'usage.warnings.productsNearLimit',
  feeds: 'usage.warnings.feedsNearLimit',
}

/**
 * Warning banner shown when nearing limits
 */
function WarningBanner({ type, percentage, onDismiss }) {
  const { t } = useTranslation('ecommerce')
  const message = t(WARNING_MESSAGES[type], { percent: percentage })

  return (
    <div className="ecommerce-usage-warning" role="alert">
      <span className="warning-icon" aria-hidden="true">⚠️</span>
      <span className="warning-text">{message}</span>
      {onDismiss && (
        <button
          className="warning-dismiss"
          onClick={onDismiss}
          aria-label={t('usage.dismissWarning', 'Dismiss warning')}
          type="button"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  )
}

WarningBanner.propTypes = {
  type: PropTypes.oneOf(['videos', 'products', 'feeds']).isRequired,
  percentage: PropTypes.number.isRequired,
  onDismiss: PropTypes.func,
}

/**
 * Limit titles and messages (defined outside component)
 */
const LIMIT_TITLES = {
  videos: 'usage.limitReached.videosTitle',
  products: 'usage.limitReached.productsTitle',
  feeds: 'usage.limitReached.feedsTitle',
}

const LIMIT_MESSAGES = {
  videos: 'usage.limitReached.videosMessage',
  products: 'usage.limitReached.productsMessage',
  feeds: 'usage.limitReached.feedsMessage',
}

/**
 * Limit reached modal with focus trap
 */
function LimitReachedModal({ type, resetDate, onClose }) {
  const { t } = useTranslation('ecommerce')
  const modalRef = useRef(null)
  const closeButtonRef = useRef(null)

  const formattedDate = resetDate
    ? formatResetDate(resetDate, { month: 'long', day: 'numeric' })
    : t('usage.nextMonth', 'next month')

  const title = t(LIMIT_TITLES[type])
  const message = t(LIMIT_MESSAGES[type], { date: formattedDate })

  // Focus trap and keyboard handling
  useEffect(() => {
    closeButtonRef.current?.focus()

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose()
  }, [onClose])

  return (
    <div
      className="ecommerce-limit-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="limit-modal-title"
      aria-describedby="limit-modal-message"
    >
      <div className="ecommerce-limit-modal" ref={modalRef}>
        <div className="limit-modal-icon" aria-hidden="true">⚠️</div>
        <h3 id="limit-modal-title" className="limit-modal-title">{title}</h3>
        <p id="limit-modal-message" className="limit-modal-message">{message}</p>
        <div className="limit-modal-actions">
          {type !== 'videos' && (
            <Link to="/subscription" className="btn-upgrade">
              {t('usage.upgradeButton', 'Upgrade Plan')}
            </Link>
          )}
          <button
            ref={closeButtonRef}
            className="btn-close"
            onClick={onClose}
            type="button"
          >
            {t('usage.understoodButton', 'Understood')}
          </button>
        </div>
      </div>
    </div>
  )
}

LimitReachedModal.propTypes = {
  type: PropTypes.oneOf(['videos', 'products', 'feeds']).isRequired,
  resetDate: PropTypes.string,
  onClose: PropTypes.func.isRequired,
}

/**
 * E-Commerce Usage Indicator
 * Shows usage stats for videos, products, and feeds with warnings at 80%
 */
function EcommerceUsageIndicator({ compact = false }) {
  const { t } = useTranslation('ecommerce')
  const { videos, products, feeds, hasAnyWarning, loading, error, mutate } = useEcommerceUsage()
  const accountId = useSelectedAccountId()
  const { hasFeature: hasEcommerceVideos } = useHasFeature('ecommerce_products_basic', accountId)
  const [dismissedWarnings, setDismissedWarnings] = useState({})
  const [showLimitModal, setShowLimitModal] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    try {
      await mutate()
    } finally {
      setTimeout(() => setIsRefreshing(false), 500)
    }
  }, [mutate])

  const dismissWarning = useCallback((type) => {
    setDismissedWarnings((prev) => ({ ...prev, [type]: true }))
  }, [])

  // Auto-show modal when limit is exceeded; auto-close when resolved
  const quotaTypes = [
    { type: 'videos', exceeded: hasEcommerceVideos && videos.isExceeded, resetDate: videos.resetDate },
    { type: 'products', exceeded: products.isExceeded, resetDate: null },
    { type: 'feeds', exceeded: feeds.isExceeded, resetDate: null },
  ]

  useEffect(() => {
    if (loading) return

    if (showLimitModal) {
      const current = quotaTypes.find((q) => q.type === showLimitModal.type)
      if (!current?.exceeded) {
        setShowLimitModal(null)
      }
      return
    }

    const firstExceeded = quotaTypes.find((q) => q.exceeded)
    if (firstExceeded) {
      setShowLimitModal({ type: firstExceeded.type, resetDate: firstExceeded.resetDate })
    }
  }, [videos.isExceeded, products.isExceeded, feeds.isExceeded, videos.resetDate, showLimitModal, loading, hasEcommerceVideos])

  // Loading state
  if (loading) {
    return (
      <div className={`ecommerce-usage-indicator ${compact ? 'compact' : ''} loading`} aria-busy="true">
        <div className="skeleton-usage-item" />
        <div className="skeleton-usage-item" />
        <div className="skeleton-usage-item" />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={`ecommerce-usage-indicator ${compact ? 'compact' : ''} error`}>
        <p className="error-message">{t('usage.errorLoading', 'Failed to load usage data')}</p>
        <button className="btn-retry" onClick={handleRefresh} type="button">
          {t('usage.retry', 'Retry')}
        </button>
      </div>
    )
  }

  // Build active warnings list
  const warningCandidates = [
    { type: 'videos', gated: hasEcommerceVideos, ...videos },
    { type: 'products', gated: true, ...products },
    { type: 'feeds', gated: true, ...feeds },
  ]
  const activeWarnings = warningCandidates
    .filter((w) => w.gated && w.isNearLimit && !w.isExceeded && !dismissedWarnings[w.type])
    .map((w) => ({ type: w.type, percentage: w.percentage }))

  return (
    <>
      <div className={`ecommerce-usage-indicator ${compact ? 'compact' : ''} ${hasAnyWarning ? 'has-warning' : ''}`}>
        <div className="usage-header">
          <h4 className="usage-title">
            {t('usage.title', 'E-Commerce Usage')}
          </h4>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="usage-refresh-button"
            title={t('usage.refresh', 'Refresh')}
            type="button"
            aria-label={t('usage.refresh', 'Refresh usage data')}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={isRefreshing ? 'spinning' : ''}
              aria-hidden="true"
            >
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" />
            </svg>
          </button>
        </div>

        {/* Warning banners */}
        {activeWarnings.map((warning) => (
          <WarningBanner
            key={warning.type}
            type={warning.type}
            percentage={warning.percentage}
            onDismiss={() => dismissWarning(warning.type)}
          />
        ))}

        <div className="usage-body">
          {hasEcommerceVideos && (
            <QuotaItem
              icon="🎬"
              label={t('usage.videos', 'Videos')}
              used={videos.used}
              limit={videos.limit}
              unlimited={videos.unlimited}
              percentage={videos.percentage}
              isNearLimit={videos.isNearLimit}
              isExceeded={videos.isExceeded}
              resetDate={videos.resetDate}
            />
          )}
          <QuotaItem
            icon="📦"
            label={t('usage.products', 'Products')}
            used={products.used}
            limit={products.limit}
            unlimited={products.unlimited}
            percentage={products.percentage}
            isNearLimit={products.isNearLimit}
            isExceeded={products.isExceeded}
          />
          <QuotaItem
            icon="📡"
            label={t('usage.feeds', 'Feeds')}
            used={feeds.used}
            limit={feeds.limit}
            unlimited={feeds.unlimited}
            percentage={feeds.percentage}
            isNearLimit={feeds.isNearLimit}
            isExceeded={feeds.isExceeded}
          />
        </div>
      </div>

      {/* Limit reached modal */}
      {showLimitModal && (
        <LimitReachedModal
          type={showLimitModal.type}
          resetDate={showLimitModal.resetDate}
          onClose={() => setShowLimitModal(null)}
        />
      )}
    </>
  )
}

EcommerceUsageIndicator.propTypes = {
  compact: PropTypes.bool,
}

export default EcommerceUsageIndicator
